import React, { useRef, useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import MonthCalendar from './MonthCalendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

// Date field: a button that opens the shared month calendar (same design as the
// date-range filter). Works inside Radix Dialog (non-modal popover, high z-index).
// - value: ISO date string (YYYY-MM-DD) or '' for empty
// - onChange: (newValue: string) => void
export default function DatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  disabled = false,
  required = false,
  className = '',
  id,
  ...rest // e.g. data-testid, passed to the button
}) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const date = value ? new Date(value + 'T00:00:00') : undefined;
  const label = date
    ? date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : placeholder;

  return (
    <div className="relative">
    {required && (
      // Invisible twin over the button: the browser's own "required" check still stops the
      // form (as the native date field did) and its bubble points at the button.
      <input
        tabIndex={-1}
        aria-hidden="true"
        className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
        required
        value={value || ''}
        onChange={() => {}}
        onFocus={() => btnRef.current?.focus()}
      />
    )}
    <Popover modal={false} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          {...rest}
          ref={btnRef}
          id={id}
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            'w-full justify-start text-left font-normal h-10',
            !date && 'text-gray-400',
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-gray-500" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(calc(100vw-16px),320px)] p-3 z-[200] bg-white shadow-xl border rounded-2xl"
        align="start"
        sideOffset={6}
        collisionPadding={8}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <MonthCalendar
          selected={value || undefined}
          onSelect={(d) => {
            onChange(d);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
    </div>
  );
}
